import React from 'react';
import { useAuth } from '../hooks/useAuth';

interface RoleGateProps {
  /** Show children only if the active role is one of these */
  allow?: string[];
  /** Show children only if the active role is NOT one of these */
  deny?: string[];
  /** Alternatively, pass a boolean condition directly */
  when?: boolean;
  children: React.ReactNode;
  /** Optional fallback to render when access is denied */
  fallback?: React.ReactNode;
}

/**
 * Conditionally render children based on the user's active role.
 *
 * Usage:
 *   <RoleGate allow={['STAFF', 'LOCATION_ADMIN', 'ORG_ADMIN']}>
 *     <AttendanceSection />
 *   </RoleGate>
 *
 *   <RoleGate deny={['PARENT']}>
 *     <AdminPanel />
 *   </RoleGate>
 *
 *   <RoleGate when={isAdmin && !isOrgLevel}>
 *     <LocationSpecificAdmin />
 *   </RoleGate>
 */
const RoleGate: React.FC<RoleGateProps> = ({ allow, deny, when, children, fallback = null }) => {
  const { activeRole } = useAuth();

  let hasAccess: boolean;

  if (when !== undefined) {
    hasAccess = when;
  } else if (allow) {
    hasAccess = allow.includes(activeRole);
  } else if (deny) {
    hasAccess = !deny.includes(activeRole);
  } else {
    hasAccess = true;
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

export default RoleGate;
